import {
  computed,
  effect,
  inject,
  Injectable,
  InjectionToken,
  signal,
  untracked,
} from '@angular/core';
import { fromEvent, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { filter } from 'rxjs/operators';

export type ThemeOptions = {
  theme: ThemeColor;
  mode: ThemeMode;
  layout: ThemeLayout;
  showThemePicker: boolean;
  showModePicker: boolean;
  showLayoutPicker: boolean;
  persistConfig: boolean;
};

const defaultInitialConfig: ThemeOptions = {
  // Línea gráfica IBSOA: una sola paleta (`theme-default` = IBSOA #00A8D8),
  // tema siempre claro y layout fijo (sidebar). Se ocultan los tres selectores
  // (color/modo/layout) para no exponer personalización de tema al usuario;
  // con `showThemePicker`/`showModePicker`/`showLayoutPicker` en false, el
  // bloque de ajustes del menú de usuario no se renderiza.
  theme: 'default',
  mode: 'light',
  layout: 'sidebar',
  showThemePicker: false,
  showModePicker: false,
  showLayoutPicker: false,
  persistConfig: true,
} as const;

export const THEME_CONFIG = new InjectionToken<Partial<ThemeOptions>>(
  'Defines theme global config provider',
  {
    providedIn: 'root',
    factory: () => defaultInitialConfig,
  },
);

export const CUSTOM_SCREENS = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
} as const;

export const ALL_THEMES_COLORS = [
  'default',
  'blue',
  'orange',
  'red',
  'violet',
  'slate',
  'green',
  'yellow',
] as const;
export const ALL_THEMES_MODES = ['light', 'dark', 'system'] as const;

export type ThemeColor = (typeof ALL_THEMES_COLORS)[number];
export type ThemeMode = (typeof ALL_THEMES_MODES)[number];
export type ThemeLayout = 'sidebar' | 'topbar';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  public static readonly LS_THEME_COLOR = 'theme-color';
  public static readonly LS_THEME_DARK_MODE = 'theme-mode';
  public static readonly LS_THEME_LAYOUT = 'theme-layout';
  readonly config = Object.assign(defaultInitialConfig, inject(THEME_CONFIG));

  private readonly colorSchemeDestroyer$ = new Subject<void>();
  private readonly colorScheme$ = fromEvent<MediaQueryListEvent>(
    window.matchMedia('(prefers-color-scheme: dark)'),
    'change',
  ).pipe(
    filter(() => 'system' === this.mode),
    takeUntil(this.colorSchemeDestroyer$),
    tap((value) => (this.darkMode = value.matches)),
  );

  private readonly _color = signal<ThemeColor>(this.config.theme);
  private readonly _mode = signal<ThemeMode>(this.config.mode);
  private readonly _darkMode = signal<boolean>(false);
  private readonly _layout = signal<ThemeLayout>(this.config.layout);

  constructor() {
    const colorSchemeSub = new Subscription();
    effect(() => {
      const themeMode = this.mode;

      untracked(() => {
        if ('system' === themeMode) {
          colorSchemeSub.unsubscribe();
          colorSchemeSub.add(this.colorScheme$.subscribe());
          this.darkMode = window.matchMedia(
            '(prefers-color-scheme: dark)',
          ).matches;
          return;
        }

        this.colorSchemeDestroyer$.next();
        this.darkMode = 'dark' === themeMode;
      });
    });

    //use local storage for persist relevant config
    if (this.config.persistConfig) {
      this.retrieveStorageConfig();
      return;
    }

    //clear storage config if not persisting
    this.clearStorageConfig();
  }

  retrieveStorageConfig() {
    const storedThemeColor = localStorage.getItem(ThemeService.LS_THEME_COLOR);
    const storedThemeMode = localStorage.getItem(
      ThemeService.LS_THEME_DARK_MODE,
    );
    const storedThemeLayout = localStorage.getItem(
      ThemeService.LS_THEME_LAYOUT,
    );

    // El color y el modo solo se restauran si su selector sigue expuesto. Con la
    // paleta/tema fijados (IBSOA, claro), un valor viejo en localStorage (p.ej.
    // `dark` o un color previo) NO debe sobreescribir la config fija: se ignora y
    // se limpia la clave obsoleta.
    if (this.config.showThemePicker && storedThemeColor) {
      this.color = storedThemeColor as ThemeColor;
    } else if (storedThemeColor) {
      localStorage.removeItem(ThemeService.LS_THEME_COLOR);
    }
    if (this.config.showModePicker && storedThemeMode) {
      this._mode.set(storedThemeMode as ThemeMode);
    } else if (storedThemeMode) {
      localStorage.removeItem(ThemeService.LS_THEME_DARK_MODE);
    }
    if (this.config.showLayoutPicker && storedThemeLayout) {
      this._layout.set(storedThemeLayout as ThemeLayout);
    } else if (storedThemeLayout) {
      localStorage.removeItem(ThemeService.LS_THEME_LAYOUT);
    }
  }

  clearStorageConfig() {
    localStorage.removeItem(ThemeService.LS_THEME_DARK_MODE);
    localStorage.removeItem(ThemeService.LS_THEME_COLOR);
    localStorage.removeItem(ThemeService.LS_THEME_LAYOUT);
  }

  set color(value: ThemeColor) {
    if (this.config.persistConfig) {
      localStorage.setItem(ThemeService.LS_THEME_COLOR, value);
    }
    this._color.set(value);
  }

  get color() {
    return this._color();
  }

  get darkMode(): boolean {
    return this._darkMode();
  }

  set darkMode(value: boolean) {
    this._darkMode.set(value);
  }

  get mode(): ThemeMode {
    return this._mode();
  }

  set mode(value: ThemeMode) {
    if (this.config.persistConfig) {
      localStorage.setItem(ThemeService.LS_THEME_DARK_MODE, value);
    }
    this._mode.set(value);
  }

  theme = computed(() => {
    return `theme-${this._color()} ${this._darkMode() ? 'dark' : ''}`;
  });

  icon = computed(() => {
    switch (this.mode) {
      case 'dark':
        return 'lucideMoon';
      case 'light':
        return 'lucideSun';
      default:
        return 'lucideSunMoon';
    }
  });

  get layout() {
    return this._layout();
  }
  set layout(value: ThemeLayout) {
    if (this.config.persistConfig) {
      localStorage.setItem(ThemeService.LS_THEME_LAYOUT, value);
    }
    this._layout.set(value);
  }

  get isSidebarTemplate() {
    return 'sidebar' === this.layout;
  }
}
